<template>
  <div>
    <reservations-item
      v-if="!!itemData.id"
      v-model="openReservation"
      :existing-item-data="itemData"
      :on-close="(item) => {
        openReservation = undefined
        if (item) itemData = deepClone(item)
      }"
    />
    <key-refresher
      ref="keyFresher"
      unique-key="add-`servers`-dialog"
    >
      <template v-slot="{ refreshKey }">
        <add-servers-dialog
          :key="refreshKey"
          v-model="showAddServers"
          v-bind="{date, forEvent}"
          :tables="reservation.tableAssignments"
          :reservation-id="reservation.id"
          :meal-period-id="reservation.mealPeriodId"
          :event-id="event.id"
          @saved="refreshServers"
          @close="() => {
            if ($refs.keyFresher) $refs.keyFresher.refresh()
          }"
        />
      </template>
    </key-refresher>
    <key-refresher
      ref="confirmation"
      unique-key="server-list-change-confirmation"
    >
      <template v-slot="{ refreshKey }">
        <confirm-server-list-change-dialog
          :key="refreshKey"
          :value="showConfirmServerListChange"
          :verbiage="['Remove', 'from']"
          :server-ids="[selectedServerId]"
          :date="date"
          :reservation-id="reservation.id"
          :allow-by-table="!!reservation.tables.length"
          @saved="refreshServers"
          @input="(open) => {
            showConfirmServerListChange = open
            if (!open) {
              const ref = $refs.confirmation
              if (ref) ref.refresh(200)
            }
          }"
        />
      </template>
    </key-refresher>
    <reservation-status-dialog
      v-model="showReservationStatusDialog"
      :reservation-status="itemData.status"
      :table-assignments="itemData.tableAssignments"
      @set-reservation-status="itemData.status = $event"
    />
    <left-side-panel-header
      :key="keys.lockTimer"
      title="Selected Table"
      compact-mode-breakpoint="md"
      :is-dirty="isDirty"
      @click:back="clickBack"
      @click:save="savePageData(itemData)"
    >
      <template #lock-timer>
        <lock-timer
          v-bind="getLockTimerSettings(
            [itemData],
            {
              recordType: 'diningReservation',
              children: true,
              include_fields: [
                'status',
                'multiplier',
                'endTime',
                'cancelledDetails',
                'calledBackBy',
                'diningReservationDetail.notificationRcpts',
                'diningReservationDetail.memberNumber',
                'diningReservationDetail.cancelledBy',
                'diningReservationDetail.username',
                'diningReservationDetail.member'
              ]
            },
            () => $emit('deselect-reservation'),
          )"
        />
      </template>
    </left-side-panel-header>
    <api-errors :errors="errors" />
    <loading-bar v-if="!!awaitingApiResponse" />
    <div v-else>
      <v-row
        no-gutters
        class="px-4"
      >
        <v-col cols="3">
          Covers
        </v-col>
        <v-col
          cols="6"
          md="3"
        >
          Reservee
        </v-col>
        <v-col
          v-if="$vuetify.breakpoint.mdAndUp"
          cols="3"
        >
          Details
        </v-col>
        <v-col
          cols="3"
          class="d-flex justify-end text-no-wrap"
        >
          Table #
        </v-col>
      </v-row>
      <reservation-card
        :reservation="itemData"
        class="ma-2"
        @click="openReservation = itemData.id"
      />
      <v-expand-transition hide-on-leave>
        <div class="error--text caption pl-3 mb-n3 mt-n1">
          {{ errorMessage }}
        </div>
      </v-expand-transition>
      <v-divider class="ma-5" />
      <v-row no-gutters>
        <v-col
          align-self="center"
          cols="12"
          md="6"
          class="px-3"
        >
          <v-card
            :class="[
              {'blue lighten-1': clickHighlightsMap['reservationStatus'] === true},
              `${getReservationStatusTextColor(reservationStatusOptionsMap[itemData.status])}--text`
            ]"
            :color="getReservationStatusColor(reservationStatusOptionsMap[itemData.status])"
            class="d-flex flex-nowrap justify-center smooth-background"
            style="opacity: 80%;"
            @click="() => {
              showReservationStatusDialog = true
              clickHighlight('reservationStatus')
            }"
          >
            <v-icon
              :class="`${getReservationStatusTextColor(reservationStatusOptionsMap[itemData.status])}--text`"
              class="pl-2 pr-1"
            >
              {{ ((reservationStatusOptionsMap[itemData.status] || {}).extData || {}).icon }}
            </v-icon>
            <v-card-title
              class="body-1 font-weight-bold"
              style="word-break: keep-all;"
            >
              {{ (reservationStatusOptionsMap[itemData.status] || {}).text }}
            </v-card-title>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="px-3"
        >
          <v-card
            :class="{'primary lighten-2': expandChangeTable}"
            class="smooth-background"
            @click="expandChangeTable = !expandChangeTable"
          >
            <v-card-title
              class="d-flex justify-center body-1 font-weight-bold flex-nowrap"
              style="word-break: break-word;"
            >
              <v-icon
                v-if="reservation.hasOverbookedTable"
                class="mr-2"
                color="warning"
              >
                mdi-alert
              </v-icon>
              Change Table
              <rotator :rotated="expandChangeTable">
                <v-icon class="pl-1 black--text">
                  mdi-chevron-down
                </v-icon>
              </rotator>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
      <v-slide-y-transition>
        <div v-if="expandChangeTable">
          <v-divider class="mt-3" />
          <change-table-expanded
            :value="itemData.tableAssignments"
            :location-id="reservation.locationId"
            :reservation-id="reservation.id"
            :reservation-date-time="reservation.dateTime"
            :table-config-id="tableConfigId"
            :date="date"
            :time="time"
            @input="$set(itemData, 'tableAssignments', $event)"
          />
          <v-divider class="mb-3" />
        </div>
      </v-slide-y-transition>
      <v-row no-gutters>
        <v-col
          align-self="center"
          cols="12"
          md="6"
          class="pt-3 px-3"
        >
          <v-card
            :class="{'primary lighten-2': itemData.status === 'FINISHED'}"
            class="smooth-background"
            @click="itemData.status = 'FINISHED'"
          >
            <div class="pa-3 text-center font-weight-bold">
              Finished
            </div>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="pt-3 px-3"
        >
          <v-card
            :class="{'primary lighten-2': itemData.status === 'NEEDS_BUSSING'}"
            class="smooth-background"
            @click="itemData.status = 'NEEDS_BUSSING'"
          >
            <div
              class="pa-3 text-center font-weight-bold flex-nowrap"
              style="word-break: break-word;"
            >
              Bussing Needed
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row
        v-if="autoClearReservationStatus"
        no-gutters
      >
        <v-card
          width="100%"
          class="font-weight-bold text-center my-2 mx-6 pa-2"
          v-on="itemData.timeExtension != null ? {} : {click: () => {itemData.timeExtension = 15}}"
        >
          <div v-if="itemData.timeExtension == null">
            + Add Time Extension
          </div>
          <div
            v-else
            class="d-flex justify-space-around"
          >
            <v-btn
              icon
              @click="() => {itemData.timeExtension = Math.max((itemData.timeExtension || 0) - 5, 0)}"
            >
              <v-icon>mdi-minus</v-icon>
            </v-btn>
            <span class="align-self-center">{{ `${itemData.timeExtension || 0} minutes` }}</span>
            <v-btn
              icon
              @click="() => {itemData.timeExtension = (itemData.timeExtension || 0) + 5}"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </v-card>
      </v-row>
      <v-divider class="mt-2 mx-5 mb-5" />
      <scaler y-axis="1.3">
        <template v-slot="{ fontSize }">
          <div
            :style="{fontSize}"
            class="font-weight-bold text-center"
          >
            Assigned Server
          </div>
        </template>
      </scaler>
      <v-card
        v-if="!assignedServers.length"
        class="pa-3 ma-2 d-flex justify-center font-weight-bold"
      >
        No Server Assigned!
      </v-card>
      <template v-else>
        <v-card
          v-for="server in assignedServers"
          :key="'assigned-server-' + server.id"
          class="pa-3 ma-2"
          :style="{display: 'grid', 'grid-template-columns': 'auto 1fr auto'}"
        >
          <v-icon class="transparent--text">
            mdi-close
          </v-icon>
          <div class="d-flex justify-center">
            <server-initial-icon
              :server="server"
              large
              class="pr-2"
            />
            <div
              :class="{'text-decoration-line-through': !server.active}"
              class="d-flex align-self-center title"
            >
              {{ server.name }}
            </div>
          </div>
          <v-hover v-slot="{ hover }">
            <v-btn
              icon
              :color="hover ? 'error' : 'grey lighten-1'"
              @click="() => {
                selectedServerId = server.id
                showConfirmServerListChange = true
              }"
            >
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-hover>
        </v-card>
      </template>
      <v-card
        class="my-2 mx-6 py-2"
        @click="showAddServers = true"
      >
        <div class="font-weight-bold text-center">
          + Add Servers
        </div>
      </v-card>
      <div class="text-center">
        Next Party
      </div>
      <reservation-card
        v-if="nextPartyReservation"
        :reservation="nextPartyReservation"
        :ripple="false"
        class="normal-cursor ma-2"
      />
      <v-card
        v-else
        class="text-center font-weight-bold pa-3 ma-2"
      >
        No Next Party Found
      </v-card>
      <div v-if="(reservation.tableAssignments || []).length > 1">
        <simple-expansion-card
          :value="false"
          class="pb-1"
        >
          <template #title>
            <div class="text-no-wrap text-truncate">
              Schedule of Tables
            </div>
          </template>
          <template #persistent-content>
            <div class="pl-4">
              {{ firstReservationTable.locationTableName }}
            </div>
            <table-schedule-chart
              :table-id="firstReservationTable.locationTableId"
              :table-name="firstReservationTable.locationTableName"
              :min="getMealPeriodStartTime(mealPeriod)"
              :max="getMealPeriodEndTime(mealPeriod)"
              :reservation-length="step"
              :meal-period-id="mealPeriod.id"
              :date="date"
              :selected-reservation="reservation"
            />
          </template>
          <template v-for="(table, index) in reservation.tableAssignments">
            <div
              v-if="index !== 0"
              :key="'table-assignment-' + table.locationTableId"
            >
              <div class="pl-4">
                {{ table.locationTableName }}
              </div>
              <table-schedule-chart
                :table-id="table.locationTableId"
                :table-name="table.locationTableName"
                :min="getMealPeriodStartTime(mealPeriod)"
                :max="getMealPeriodEndTime(mealPeriod)"
                :reservation-length="step"
                :meal-period-id="mealPeriod.id"
                :date="date"
                :selected-reservation="reservation"
              />
            </div>
          </template>
        </simple-expansion-card>
      </div>
      <div v-else>
        <div class="text-center">
          Schedule of Table
        </div>
        <table-schedule-chart
          :key="keys.tableScheduleChart"
          :table-id="firstReservationTable.locationTableId"
          :table-name="firstReservationTable.locationTableName"
          :min="getMealPeriodStartTime(mealPeriod)"
          :max="getMealPeriodEndTime(mealPeriod)"
          :reservation-length="step"
          :meal-period-id="mealPeriod.id"
          :date="date"
          :selected-reservation="reservation"
        />
      </div>
      <simple-expansion-card :value="false">
        <template #title>
          <div class="text-no-wrap text-truncate">
            Other Table Reservations
          </div>
        </template>
        <v-row
          no-gutters
          class="px-4"
        >
          <v-col cols="3">
            Covers
          </v-col>
          <v-col
            cols="6"
            md="3"
          >
            Reservee
          </v-col>
          <v-col
            v-if="$vuetify.breakpoint.mdAndUp"
            cols="3"
          >
            Details
          </v-col>
          <v-col
            cols="3"
            class="d-flex justify-end text-no-wrap"
          >
            Table #
          </v-col>
        </v-row>
        <div v-if="!otherTableReservations.length">
          <v-card class="elevation-0 ma-2 text-center font-weight-bold">
            No Other Table Reservations Found...
          </v-card>
        </div>
        <div v-else>
          <reservation-card
            v-for="otherTableReservation in otherTableReservations"
            :key="'other-table-reservation-' + otherTableReservation.id"
            :ripple="false"
            :reservation="otherTableReservation"
            class="ma-2 normal-cursor"
          />
        </div>
      </simple-expansion-card>
    </div>
  </div>
</template>

<script>
import {deepClone} from '@/ft-vue-core/util/helpers'
import recordLockHelper from '@/ft-vue-core/mixins/recordLockHelper'
import ConfirmServerListChangeDialog from './ConfirmServerListChangeDialog'
import AddServersDialog from './AddServersDialog'
import floorPlanHelpers from '../mixins/floorPlanHelpers'
import ReservationCard from './reservationsTab/ReservationCard'
import ChangeTableExpanded from '../helperComponents/ChangeTableExpanded'
import TableScheduleChart from '../helperComponents/TableScheduleChart'
import mealPeriodHelpers from '../mixins/mealPeriodHelpers'
import ReservationStatusDialog from './ReservationStatusDialog'
import Rotator from '@/ft-vue-core/components/transformers/Rotator'
import Scaler from '@/ft-vue-core/components/transformers/Scaler'
import ReservationsItem from '../../reservations/ReservationsItem'
export default {
  name: 'SelectedReservation',
  components: {
    ConfirmServerListChangeDialog,
    AddServersDialog,
    ReservationCard,
    ChangeTableExpanded,
    TableScheduleChart,
    ReservationStatusDialog,
    Rotator,
    Scaler,
    ReservationsItem,
  },
  mixins: [floorPlanHelpers, recordLockHelper, mealPeriodHelpers],
  props: {
    reservation: {
      type: Object,
      default () {return {}}
    },
    mealPeriod: {
      type: Object,
      default () {return {}}
    },
    event: {
      type: Object,
      default () {return {}}
    },
    forEvent: Boolean,
    date: [String, Number],
    time: [String, Number],
    autoClearReservationStatus: Boolean,
    tableConfigId: Number,
  },
  data () {
    return {
      openReservation: undefined,
      apiRecordCommand: 'diningReservation',
      clickHighlightsMap: {},
      expandChangeTable: false,
      itemData: {},
      assignedServers: [],
      otherTableReservations: [],
      nextPartyReservation: undefined,
      reservationStatusOptions: [],
      showReservationStatusDialog: false,
      keys: {
        tableScheduleChart: 'table-schedule-chart',
        lockTimer: 'lock-timer',
      },
      selectedServerId: undefined,
      showAddServers: false,
      showConfirmServerListChange: false,
      awaitingApiRefresh: 0,
      isDirty: false,
      errorMessage: undefined
    }
  },
  computed: {
    firstReservationTable () {
      return this.reservation.tableAssignments?.[0] ?? {}
    },
    step () {
      return this.mealPeriod?.timeIncrement || 30
    },
    reservationStatusOptionsMap () {
      return this.reservationStatusOptions?.reduce((acc, r) => {
        acc[r.value] = r
        return acc
      }, {}) ?? {}
    }
  },
  watch: {
    awaitingApiRefresh (newVal, oldVal) {
      if (newVal === 0 && oldVal != null) {
        this.$set(this.keys, 'tableScheduleChart', this.keyGenerator.next().value)
        this.$set(this.keys, 'lockTimer', this.keyGenerator.next().value)
      }
    }
  },
  mounted () {
    const {time, date, tableAssignments, hasOverbookedTable} = this.reservation
    if (hasOverbookedTable) this.errorMessage = '* Reservation has an Overbooked Table'
    const commands = [
      this.getSelectedReservationCommand(),
      {
        command: 'apiFieldOption',
        load: this.reservationStatusOptions,
        afterLoad: () => this.awaitingApiResponse -= 1,
        error: (r) => {
          this.awaitingApiResponse -= 1
          this.errors.push(r.message)
        },
        loading: this.isLoading,
        params: {filter_methods: {forField: [this.apiRecordCommand, 'status']}}
      },
      {
        command: 'diningReservation',
        load: r => this.nextPartyReservation = r[0],
        afterLoad: () => this.awaitingApiResponse -= 1,
        error: r => {
          this.awaitingApiResponse -= 1
          this.errors.push(r.message)
        },
        params: {
          children: true,
          order_by: ['dateTime:ASC'],
          include_fields: ['diningReservationDetail.member'],
          filter_methods: {
            filterByAfterTime: [time],
            filterByDate: [date],
            filterByTableAssignments: [tableAssignments?.map(t => t.locationTableId) ?? []]
          }
        }
      },
      this.getOtherTableReservationsCommand(),
      this.getAssignedServersCommand()
    ]
    this.awaitingApiResponse = commands.length
    this.$ftAPI().get(this.addResourceErrorHandler(commands))
  },
  beforeDestroy () {
    this.$ftAPI().get(this.addResourceErrorHandler({
      command: 'releaseLock',
      params: {record_key: this.itemData.recordLockKey}
    }))
  },
  methods: {
    deepClone,
    getReservationStatusColor (status = {}) {
      const {fillColor} = status.extData ?? {}
      return fillColor
    },
    getReservationStatusTextColor (status = {}) {
      const {textColor} = status.extData ?? {}
      return textColor
    },
    clickBack (isDirty, confirmed) {
      if (isDirty && !confirmed) {
        this.$vPrompt({
          title: 'Unsaved Changes',
          titleIcon: 'mdi-alert-circle',
          titleIconColor: 'warning',
          text: 'Discard unsaved changes to this Reservation?',
          confirmText: 'Discard',
          confirmAction: () => this.clickBack(isDirty, true),
          cancelAction: () => {}
        })
      } else this.$emit('deselect-reservation')
    },
    // findNextParty (afterLoad, error) {
    //   const {time, date, tableAssignments} = this.reservation
    //   this.$ftAPI().get({
    //     command: 'diningReservation',
    //     load: r => {
    //       this.awaitingApiResponse -= 1
    //       this.nextPartyReservation = r[0]
    //       afterLoad?.(r)
    //     },
    //     error: r => {
    //       this.awaitingApiResponse -= 1
    //       this.errors.push(r.message)
    //       error?.(r)
    //     },
    //     params: {
    //       children: true,
    //       order_by: ['dateTime:ASC'],
    //       include_fields: ['diningReservationDetail.member'],
    //       filter_methods: {
    //         filterByAfterTime: [time],
    //         filterByDate: [date],
    //         filterByTableAssignments: [tableAssignments?.map(t => t.locationTableId) ?? []],
    //       }
    //     }
    //   })
    // const {id, locationId} = this.reservation
    // this.$ftAPI().put(this.addResourceErrorHandler({
    //   command: 'diningFindNextReservation',
    //   loading: this.isLoading,
    //   afterLoad: (r) => {
    //     this.awaitingApiResponse -= 1
    //     this.nextPartyReservation = r?.[0]?.nextReservation
    //     afterLoad?.(r)
    //   },
    //   error: (r) => {
    //     this.awaitingApiResponse -= 1
    //     this.errors.push(r.message)
    //     error?.(r)
    //   },
    // }), {reservationId: id, locationId})
    // },
    getSelectedReservationCommand (afterLoad, error) {
      return this.withResourceLockHandler({
        command: this.apiRecordCommand,
        loading: this.isLoading,
        afterLoad: (r) => {
          this.itemData = r?.[0] ?? {}
          this.awaitingApiResponse -= 1
          this.isDirty = false
          afterLoad?.(r)
        },
        error: (r) => {
          this.awaitingApiResponse -= 1,
          this.errors.push(r.message)
          error?.(r)
        },
        params: {
          children: true,
          include_fields: [
            'status',
            'multiplier',
            'endTime',
            'cancelledDetails',
            'calledBackBy',
            'diningReservationDetail.notificationRcpts',
            'diningReservationDetail.memberNumber',
            'diningReservationDetail.cancelledBy',
            'diningReservationDetail.username',
            'diningReservationDetail.member'
          ],
          filter: [{field: 'id', operator: 'EQUAL', values: [this.reservation.id]}]
        }
      })
    },
    getOtherTableReservationsCommand (afterLoad, error) {
      return {
        command: this.apiRecordCommand,
        load: this.otherTableReservations,
        loading: this.isLoading,
        afterLoad: (r) => {
          this.awaitingApiResponse -= 1
          afterLoad?.(r)
        },
        error: (r) => {
          this.awaitingApiResponse -= 1
          this.errors.push(r.message)
          error?.(r)
        },
        params: {
          children: true,
          filter_methods: Object.assign(
            {
              filterOutTableAssignment: [this.reservation.tableAssignments?.map(t => t.locationTableId)],
              filterByDate: [this.date],
            },
            this.forEvent ? {filterEvent: [this.event.id]} : {filterMealPeriod: [this.mealPeriod.id]}
          )
        }
      }
    },
    getAssignedServersCommand (afterLoad, error) {
      return {
        command: 'diningServer',
        load: this.assignedServers,
        loading: this.isLoading,
        afterLoad: (r) => {
          this.awaitingApiResponse -= 1
          afterLoad?.(r)
        },
        error: (r) => {
          this.awaitingApiResponse -= 1
          this.errors.push(r.message)
          error?.(r)
        },
        params: {
          children: true,
          filter_methods: this.forEvent
            ? {filterByEventAssignment: [this.event.id, this.reservation.id, this.reservation.tableAssignments.map(a => a.locationTableId)]}
            : {filterByAssignment: [this.reservation.date, this.reservation.id, this.reservation.tableAssignments.map(a => a.locationTableId)]}
        }
      }
    },
    clickHighlight (key) {
      this.$set(this.clickHighlightsMap, key, true)
      setTimeout(() => {
        this.$set(this.clickHighlightsMap, key, false)
      }, 500)
    },
    savePageData (itemData) {
      this.$ftAPI().put({
        command: 'diningSaveReservationWithStatus',
        loading: this.isLoading,
        error: (r) => {
          this.errors.push(r.message)
          this.errorMessage = r.field_errors.reduce((acc, r) => {
            const {status} = r
            if (!acc.length) return status
            return `${acc}, ${status}`
          }, '')
        },
        afterLoad: () => this.$emit('deselect-reservation'),
        params: {
          children: true,
          include_fields: ['status']
        }
      }, {reservation: itemData, status: itemData.status})
    },
    refreshPageData () {
      const commands = [
        {
          command: 'releaseLock',
          afterLoad: () => {
            this.awaitingApiResponse -= 1
            this.awaitingApiRefresh -= 1
          },
          error: (r) => {
            this.awaitingApiResponse -= 1
            this.awaitingApiRefresh -= 1
            this.errors.push(r.message)
          },
          params: {record_key: this.itemData.recordLockKey}
        },
        this.getSelectedReservationCommand(() => this.awaitingApiRefresh -= 1),
        this.getAssignedServersCommand(() => this.awaitingApiRefresh -= 1),
        this.getOtherTableReservationsCommand(() => this.awaitingApiRefresh -= 1)
      ]
      this.awaitingApiResponse = commands.length + 1
      this.awaitingApiRefresh = commands.length + 1
      this.$ftAPI().get(this.addResourceErrorHandler(commands))
      this.findNextParty(this.reservation, this.nextPartyReservation, () => this.awaitingApiRefresh -= 1)
    },
    refreshServers () {
      this.awaitingApiResponse += 1
      this.$ftAPI().get(this.getAssignedServersCommand())
    }
  },
}
</script>

<style scoped>
.v-card::before {
  background: none;
}
.smooth-background {
  transition: background-color 0.2s ease-in-out;
}
.normal-cursor {
  cursor: default;
}
</style>
